import ReactiveState from '../../../hooks/ReactiveState';
import { FormGroupState } from './enums';

class FormGroup {
  #reactiveState;
  #validationStrategy;

  constructor(controls, validationStrategy) {
    this.#reactiveState = new ReactiveState({
      [FormGroupState.Controls]: controls,
      [FormGroupState.Error]: '',
      [FormGroupState.IsDisabled]: false,
    });

    this.setValidationStrategy(validationStrategy);
  }

  get #controlsEntries() {
    return Object.entries(this.#reactiveState.value[FormGroupState.Controls]);
  }

  get #initialControlsEntries() {
    return Object.entries(this.#reactiveState.initialState[FormGroupState.Controls]);
  }

  get value() {
    return this.#controlsEntries.reduce((acc, [controlName, control]) => ({
      ...acc,
      [controlName]: control.value,
    }), {});
  }

  get initialValue() {
    return this.#initialControlsEntries.reduce((acc, [controlName, control]) => ({
      ...acc,
      [controlName]: control.initialValue,
    }), {});
  }

  get error() {
    return this.#reactiveState.value[FormGroupState.Error];
  }

  get isDisabled() {
    return this.#reactiveState.value[FormGroupState.IsDisabled];
  }

  get isValid() {
    const areControlsValid = this.#controlsEntries.every(([, control]) => control.isValid);
    const hasFormError = !!this.error;

    return areControlsValid && !hasFormError;
  }

  get controls() {
    return this.#reactiveState.value[FormGroupState.Controls];
  }

  get controlsList() {
    return Object.values(this.#reactiveState.value[FormGroupState.Controls]);
  }

  get initialControls() {
    return this.#reactiveState.initialState[FormGroupState.Controls];
  }

  get initialControlsList() {
    return Object.values(this.#reactiveState.initialState[FormGroupState.Controls]);
  }

  get isChanged() {
    const isSomeControlChanged = this.controlsList.some((control) => control.isChanged);

    if (isSomeControlChanged) {
      return true;
    }

    return this.controlsList.length !== this.initialControlsList.length;
  }

  get isRequired() {
    return this.#controlsEntries.some(([, control]) => control.isRequired);
  }

  getControl(name) {
    return this.#reactiveState.value[FormGroupState.Controls][name] || null;
  }

  setValidationStrategy(formValidationStrategy) {
    const strategies = Array.isArray(formValidationStrategy)
      ? formValidationStrategy
      : [formValidationStrategy];
    this.#validationStrategy = strategies;

    this.#controlsEntries.forEach(([, control]) => {
      control.setValidationStrategy(strategies);
    });
  }

  addControl(name, control) {
    const hasControl = this.getControl(name);

    if (hasControl) {
      return;
    }

    this.#reactiveState.patch({
      [FormGroupState.Controls]: {
        ...this.#reactiveState.value[FormGroupState.Controls],
        [name]: control,
      },
    });

    control.setValidationStrategy(this.#validationStrategy);
  }

  addControls(controls) {
    const additionalControlsEntries = Object.entries(controls);

    additionalControlsEntries.forEach(([controlName, control]) => {
      this.addControl(controlName, control);
    });
  }

  setControls(controls) {
    const controlsNames = this.#controlsEntries.map(([name]) => name);

    this.removeControls(controlsNames);
    this.addControls(controls);
  }

  setInitialControls(controls) {
    this.setControls(controls);
    this.#reactiveState.patchInitialValue({
      [FormGroupState.Controls]: controls,
    });
  }

  removeControl(controlName) {
    const control = this.getControl(controlName);

    if (control) {
      const controls = { ...this.#reactiveState.value[FormGroupState.Controls] };

      delete controls[controlName];

      this.#reactiveState.patch({
        [FormGroupState.Controls]: controls,
      });
    }
    return control;
  }

  removeControls(controlNames) {
    controlNames.forEach((controlName) => {
      this.removeControl(controlName);
    });
  }

  setValue(newFormValue) {
    FormGroup.#iterateByValueEntries(newFormValue, ([controlName, controlValue]) => {
      this.patchValue(controlName, controlValue);
    });
  }

  setInitialValue(newInitialValue) {
    FormGroup.#iterateByValueEntries(newInitialValue, ([controlName, controlValue]) => {
      const control = this.getControl(controlName);

      control?.setInitialValue(controlValue);
    });
  }

  patchValue(controlName, controlValue) {
    const control = this.getControl(controlName);

    if (!control) {
      return;
    }

    control.setValue(controlValue);
  }

  enable() {
    this.#reactiveState.patch({
      [FormGroupState.IsDisabled]: false,
    });
    this.#controlsEntries.forEach(([, control]) => control.enable());
  }

  disable() {
    this.#reactiveState.patch({
      [FormGroupState.IsDisabled]: true,
    });
    this.#controlsEntries.forEach(([, control]) => control.disable());
  }

  reset(value) {
    this.#controlsEntries.forEach(([name, control]) => control.reset(value?.[name]));
  }

  clean() {
    this.#controlsEntries.forEach(([, control]) => control.clean());
  }

  setError(error) {
    this.#reactiveState.patch({
      [FormGroupState.Error]: error,
    });
  }

  getIsValid() {
    return this.#controlsEntries.every(([, control]) => control.getIsValid());
  }

  validate() {
    this.#controlsEntries.forEach(([, control]) => {
      control.validate();
    });
  }

  validateExceptForControls(controlNames) {
    this.#controlsEntries.forEach(([name, control]) => {
      if (!controlNames.includes(name)) {
        control.validate();
      }
    });
  }

  static #iterateByValueEntries(value, callback) {
    Object.entries(value).forEach((entry, index) => {
      callback(entry, index);
    });
  }
}

export default FormGroup;
